// =============================================================================
// Util: Vales como medio de pago en el checkout de Nueva Venta (voucher-checkout.util.ts)
// Cuánto cubren los vales cargados, qué queda por cobrar y el vale nuevo por el sobrante. Con vales
// no se aplica el descuento del medio de pago, pero sí el recargo (cuotas), solo sobre el resto (P6).
// La base revalida todo al confirmar. Ver docs/SPEC_VALE_SALDO_A_FAVOR.md §5.6.
// =============================================================================

import { ValeParaUso, ValeCargado } from '../models/voucher.model';
import { SessionVoucherSummary } from '../models/cash.model';

const redondear = (n: number) => Math.round(n * 100) / 100;

/**
 * Código con el formato guardado (V-XXXX-XXXX) a partir de lo que se escribe o escanea: sin
 * espacios ni guiones, en mayúsculas y con o sin la V inicial. null si no puede ser un vale.
 * Mismo criterio que fn_normalize_voucher_code en la base.
 */
export function codigoValeConFormato(texto: string | null | undefined): string | null {
  const n = (texto ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (n.length === 9 && n.startsWith('V')) return `V-${n.slice(1, 5)}-${n.slice(5, 9)}`;
  if (n.length === 8) return `V-${n.slice(0, 4)}-${n.slice(4, 8)}`;
  return null;
}

/** Normaliza la respuesta de get_voucher_for_use (importe como número). */
export function normalizarValeParaUso(raw: unknown): ValeParaUso {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    ok: r['ok'] === true,
    reason: (r['reason'] as string | null) ?? null,
    id: (r['id'] as string | null) ?? null,
    code: (r['code'] as string | null) ?? null,
    amount: Number(r['amount']) || 0,
    expiresAt: (r['expires_at'] as string | null) ?? null,
    clientName: (r['client_name'] as string | null) ?? null,
  };
}

/**
 * Por qué no se puede cargar el vale en esta venta (null = se puede): el motivo de la base, que
 * ya esté cargado o que los vales cargados ya cubran el total.
 */
export function motivoNoCargarVale(vale: ValeParaUso, cargados: ValeCargado[], totalLista: number): string | null {
  if (!vale.ok || !vale.id || !vale.code) return vale.reason || 'No se puede usar este vale.';
  if (cargados.some(c => c.id === vale.id)) return `El vale ${vale.code} ya está cargado.`;
  const yaCargado = cargados.reduce((s, c) => s + c.amount, 0);
  if (cargados.length > 0 && yaCargado >= totalLista) {
    return 'Los vales cargados ya cubren el total de la venta.';
  }
  return null;
}

export interface CoberturaVales {
  /** Suma de los importes de los vales cargados. */
  totalVales: number;
  /** Lo que los vales pagan de la venta (hasta el total). */
  aplicado: number;
  /** Lo que queda por cobrar a precio de lista (antes del recargo del medio, si tiene). */
  resto: number;
  /** Importe del vale nuevo que se emite si los vales superan el total. */
  sobrante: number;
  /** Los vales cubren todo: no se elige modalidad ni medio. */
  cubreTodo: boolean;
  /** Primer vale que no hace falta (los anteriores ya cubren el total), o null. */
  valeDeMas: string | null;
}

/** Cuánto cubren los vales cargados (en el orden en que se cargaron) de una venta de totalLista. */
export function coberturaVales(totalLista: number, vales: Pick<ValeCargado, 'code' | 'amount'>[]): CoberturaVales {
  const total = redondear(totalLista);
  let acumulado = 0;
  let valeDeMas: string | null = null;
  for (const v of vales) {
    if (valeDeMas === null && acumulado >= total && total > 0) valeDeMas = v.code;
    acumulado = redondear(acumulado + v.amount);
  }
  const aplicado = Math.min(acumulado, total);
  return {
    totalVales: acumulado,
    aplicado,
    resto: redondear(total - aplicado),
    sobrante: redondear(Math.max(0, acumulado - total)),
    cubreTodo: vales.length > 0 && acumulado >= total,
    valeDeMas,
  };
}

/**
 * Factor del ajuste del medio de pago cuando hay vales (P6): el descuento no se aplica (el pago es
 * en la práctica combinado), el recargo sí, porque es financiación (cuotas) de lo que se cobra.
 */
export function factorAjusteConVales(
  adjustmentType: string | null | undefined,
  adjustmentPercentage: number | string | null | undefined
): number {
  const pct = Number(adjustmentPercentage) || 0;
  return adjustmentType === 'surcharge' && pct > 0 ? 1 + pct / 100 : 1;
}

export interface PreciosConVales {
  /** Precio unitario de cada ítem (mismo orden), con el ajuste del medio prorrateado. */
  preciosUnitarios: number[];
  /** Total de la venta: vales + resto con el ajuste del medio. */
  totalVenta: number;
  /** Lo que se cobra con el medio de pago. */
  cobrar: number;
  /** Recargo del medio aplicado solo sobre el resto (0 si no hay). */
  ajuste: number;
}

/**
 * Total Caja con vales (P6): el recargo del medio (factor, p. ej. 1.1 = +10% por cuotas) se aplica
 * solo sobre lo que se cobra con ese medio; la parte del vale va a precio de lista. El total
 * resultante se prorratea en los precios unitarios (la base suma cantidad × precio), y lo que se
 * cobra sale de esa suma para que vales + cobro cierren exacto con el total de la venta.
 */
export function preciosConVales(
  items: { unitPrice: number; quantity: number }[],
  aplicado: number,
  factor: number
): PreciosConVales {
  const totalLista = redondear(items.reduce((s, i) => s + i.unitPrice * i.quantity, 0));
  if (totalLista <= 0) {
    return { preciosUnitarios: items.map(i => i.unitPrice), totalVenta: 0, cobrar: 0, ajuste: 0 };
  }
  const aplicadoReal = Math.min(redondear(aplicado), totalLista);
  const objetivo = aplicadoReal + redondear((totalLista - aplicadoReal) * factor);
  const k = objetivo / totalLista;
  const preciosUnitarios = items.map(i => redondear(i.unitPrice * k));
  const totalVenta = redondear(preciosUnitarios.reduce((s, p, idx) => s + p * items[idx].quantity, 0));
  return {
    preciosUnitarios,
    totalVenta,
    cobrar: redondear(Math.max(0, totalVenta - aplicadoReal)),
    ajuste: redondear(totalVenta - totalLista),
  };
}

// -----------------------------------------------------------------------------
// Vales en Caja Diaria (sin dependencias de formularios: lo usa cash.service)
// -----------------------------------------------------------------------------

/** Normaliza la respuesta de get_cash_session_voucher_summary (cantidades e importes como número). */
export function normalizarResumenValesCaja(raw: unknown): SessionVoucherSummary {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    usedCount: Number(r['used_count']) || 0,
    usedAmount: Number(r['used_amount']) || 0,
    issuedCount: Number(r['issued_count']) || 0,
    issuedAmount: Number(r['issued_amount']) || 0,
    carriedCount: Number(r['carried_count']) || 0,
    carriedAmount: Number(r['carried_amount']) || 0,
    usedItems: (Array.isArray(r['used_items']) ? r['used_items'] as Record<string, unknown>[] : []).map(i => ({
      code: String(i['code'] ?? ''),
      amount: Number(i['amount']) || 0,
      originBranchId: (i['origin_branch_id'] as string | null) ?? null,
      originBranchName: (i['origin_branch_name'] as string | null) ?? null,
      usedAt: (i['used_at'] as string | null) ?? null,
    })),
  };
}

/** La caja tuvo movimientos de vales (usados o emitidos). */
export function cajaTieneVales(resumen: SessionVoucherSummary | null | undefined): boolean {
  return !!resumen && (resumen.usedCount > 0 || resumen.issuedCount > 0 || resumen.carriedCount > 0);
}
